import type { Log } from "@slot/presentation";
import type { CellMultiplierBadge, CellMultiplierTarget } from "./replay/UpdateCellMultipliersActionHandler";

/** The badges as the presenter drives them: methods only (plan §2.3). */
export interface CellMultiplierPort {
  /** Shows `multiplier` on the cell (a new badge, or a badge whose value changed); resolves when its landing has played. */
  showBadge(positionIndex: number, multiplier: string): Promise<void>;
  removeBadge(positionIndex: number): void;
  clearBadges(): void;
}

/**
 * The cell multiplier badges (the `merge` rules: every cell of a winning cluster gains one after the merge; it belongs to
 * the cell, not to the symbol on it). Each `update_cell_multipliers` is the board's whole badge set: a cell that left
 * it loses its badge, a new or changed badge lands, an unchanged one stays as it is. The board starts clean at a base
 * spin — the player's spin, a base spin's boundary (a replay has no spin request), a recovery — and keeps its badges
 * through a free-spins session, whose every spin restates them.
 */
export class CellMultiplierPresenter implements CellMultiplierTarget {
  private badges = new Map<number, string>();

  public constructor(private readonly deps: { view: CellMultiplierPort; log: Log }) {}

  public async updateCellMultipliers(badges: readonly CellMultiplierBadge[]): Promise<void> {
    const next = new Map(badges.map((badge) => [badge.positionIndex, badge.multiplier]));
    for (const index of this.badges.keys()) if (!next.has(index)) this.deps.view.removeBadge(index);
    const landing = [...next].filter(([index, multiplier]) => this.badges.get(index) !== multiplier);
    this.badges = next;
    this.deps.log(`[CellMultipliers] ${[...next].map(([index, multiplier]) => `${index}:x${multiplier}`).join(" ") || "none"}`);
    await Promise.all(landing.map(([index, multiplier]) => this.deps.view.showBadge(index, multiplier)));
  }

  /** A spin's boundary: a base spin starts clean; a free spin keeps the session's badges (its reveal restates them). */
  public onSpinBoundaryStart(spinModeIdentifier: string | undefined): void {
    if (spinModeIdentifier?.toLowerCase() === "base") this.clear();
  }

  /** The board starts clean (the player's spin, a recovery). */
  public clear(): void {
    if (this.badges.size === 0) return;
    this.badges = new Map();
    this.deps.view.clearBadges();
  }
}
